import React, { useState, useEffect, useCallback, useRef } from 'react';
import { passkeyKitService, SmartAccountRecord } from '../services/passkeyKitService';

interface Props {
  walletAddress: string;
  /** Freighter wallet instance — used to sign the fund payment transaction */
  wallet?: any;
  /** Increment to force a balance re-fetch (e.g. after a vault deposit) */
  refreshTrigger?: number;
  /** Called after funding so the parent can refresh the wallet's native XLM balance */
  onWalletBalanceChange?: () => void;
}

const EXPERT_NET = process.env.REACT_APP_STELLAR_NETWORK === 'mainnet' ? 'public' : 'testnet';

function ExpertLink({ id }: { id: string }) {
  if (!id) return null;
  return (
    <a
      href={`https://stellar.expert/explorer/${EXPERT_NET}/contract/${id}`}
      target="_blank"
      rel="noopener noreferrer"
      title="View on Stellar Expert"
      style={{ color: '#2563eb', fontSize: '0.72em', textDecoration: 'none', marginLeft: '4px', verticalAlign: 'middle' }}
    >↗</a>
  );
}

const PasskeyKitPanel: React.FC<Props> = ({
  walletAddress,
  wallet,
  refreshTrigger,
  onWalletBalanceChange,
}) => {
  const [accounts, setAccounts]             = useState<SmartAccountRecord[]>([]);
  const [balances, setBalances]             = useState<Record<string, number | null>>({});
  const [isRegistering, setIsRegistering]   = useState(false);
  const [fundingId, setFundingId]           = useState<string | null>(null);
  const [fundAmount, setFundAmount]         = useState('2');
  const [isFunding, setIsFunding]           = useState(false);
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  const [error, setError]                   = useState<string | null>(null);
  const [fundSuccess, setFundSuccess]       = useState<string | null>(null);
  const [copiedId, setCopiedId]             = useState<string | null>(null);
  const [showManage, setShowManage]         = useState(false);
  const prevRefreshTrigger                  = useRef(refreshTrigger);

  // Transfer state
  const [transferFromId, setTransferFromId]     = useState<string | null>(null);
  const [transferDest, setTransferDest]         = useState<'wallet' | string>('wallet');
  const [transferAmount, setTransferAmount]     = useState('1');
  const [isTransferring, setIsTransferring]     = useState(false);
  const [transferSuccess, setTransferSuccess]   = useState<string | null>(null);

  const factoryId = process.env.REACT_APP_ACCOUNT_FACTORY_ID;

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  const reload = useCallback(() => {
    setAccounts(passkeyKitService.getAccounts().filter(a => a.walletAddress === walletAddress));
  }, [walletAddress]);

  useEffect(() => { reload(); }, [reload]);

  const fetchBalance = useCallback((acc: SmartAccountRecord) => {
    fetch(`${backendUrl}/api/passkey-kit/account-balance`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ accountAddress: acc.accountAddress }),
    })
      .then(r => r.json())
      .then(d => {
        if (d.success) setBalances(prev => ({ ...prev, [acc.credentialId]: d.balance }));
      })
      .catch(() => {});
  }, [backendUrl]);

  // Fetch balances on mount and whenever accounts change
  useEffect(() => {
    accounts.forEach(fetchBalance);
  }, [accounts, fetchBalance]);

  // Refresh balances when parent signals a deposit was made
  useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger !== prevRefreshTrigger.current) {
      prevRefreshTrigger.current = refreshTrigger;
      accounts.forEach(fetchBalance);
    }
  }, [refreshTrigger, accounts, fetchBalance]);

  if (!factoryId) return null;

  const handleRegister = async () => {
    setIsRegistering(true);
    setError(null);
    try {
      await passkeyKitService.register(`GeoTrust ${walletAddress.slice(0, 8)}`, walletAddress);
      reload();
    } catch (err: any) {
      setError(err.message || 'Registration failed');
    } finally {
      setIsRegistering(false);
    }
  };

  const handleFund = async (acc: SmartAccountRecord) => {
    const amount = parseFloat(fundAmount);
    if (!amount || amount <= 0) return;
    setIsFunding(true);
    setError(null);
    setFundSuccess(null);

    try {
      let signedXdr: string | undefined;

      if (wallet) {
        // Step 1: Ask the backend to build + simulate the SAC transfer transaction.
        // Smart account addresses are C... contracts — classic Operation.payment cannot
        // target them. We must invoke the XLM SAC transfer(from, to, amount) instead.
        const { Networks } = await import('@stellar/stellar-sdk');
        const networkPassphrase = process.env.REACT_APP_STELLAR_NETWORK === 'mainnet'
          ? Networks.PUBLIC
          : Networks.TESTNET;

        const prepResp = await fetch(`${backendUrl}/api/passkey-kit/prepare-fund`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fromAddress: walletAddress,
            toAddress: acc.accountAddress,
            amountStroops: Math.round(amount * 1e7),
          }),
        });
        const prepData = await prepResp.json();
        if (!prepData.success) throw new Error(prepData.error || 'Failed to prepare fund transaction');

        // Step 2: Sign the assembled transaction with Freighter.
        signedXdr = await wallet.signTransaction(prepData.unsignedXdr, networkPassphrase);
      }

      const body: Record<string, unknown> = signedXdr
        ? { signedXdr }
        : { accountAddress: acc.accountAddress, amountStroops: Math.round(amount * 1e7) };

      const resp = await fetch(`${backendUrl}/api/passkey-kit/fund-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await resp.json();
      if (!data.success) throw new Error(data.error || 'Funding failed');

      fetchBalance(acc);
      setFundingId(null);
      setFundAmount('2');
      setFundSuccess(`${amount} XLM sent to smart account`);
      setTimeout(() => setFundSuccess(null), 4000);
      onWalletBalanceChange?.();
    } catch (err: any) {
      setError(err.message || 'Funding failed');
    } finally {
      setIsFunding(false);
    }
  };

  const handleTransfer = async (acc: SmartAccountRecord) => {
    const amount = parseFloat(transferAmount);
    if (!amount || amount <= 0) return;
    setIsTransferring(true);
    setError(null);
    setTransferSuccess(null);

    try {
      // Resolve destination address
      const destAddress = transferDest === 'wallet' ? walletAddress : transferDest;
      const amountStroops = Math.round(amount * 1e7);

      // Step 1: Ask backend to build + simulate the SAC transfer
      const prepResp = await fetch(`${backendUrl}/api/passkey-kit/prepare-sac-transfer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ smartAccountAddress: acc.accountAddress, destinationAddress: destAddress, amountStroops }),
      });
      const prepData = await prepResp.json();
      if (!prepData.success) throw new Error(prepData.error || 'Failed to prepare transfer');

      // Step 2: Sign with passkey
      const { passkeyKitService: pks } = await import('../services/passkeyKitService');
      const signedXdr = await pks.signUnsignedXdr(prepData.unsignedXdr, acc, prepData.latestLedger);

      // Step 3: Submit via fee-pay-and-submit
      const submitResp = await fetch(`${backendUrl}/api/passkey-kit/fee-pay-and-submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ xdrWithPasskeySig: signedXdr }),
      });
      const submitData = await submitResp.json();
      if (!submitData.success) throw new Error(submitData.error || 'Transfer failed');

      fetchBalance(acc);
      setTransferFromId(null);
      setTransferSuccess(`${amount} XLM transferred successfully`);
      setTimeout(() => setTransferSuccess(null), 5000);
      onWalletBalanceChange?.();
    } catch (err: any) {
      setError(err.message || 'Transfer failed');
    } finally {
      setIsTransferring(false);
    }
  };

  const handleRemove = (acc: SmartAccountRecord) => {
    if (confirmRemoveId === acc.credentialId) {
      const remaining = passkeyKitService.getAccounts().filter(a => a.credentialId !== acc.credentialId);
      localStorage.setItem('geotrust_passkey_kit_accounts', JSON.stringify(remaining));
      reload();
      setConfirmRemoveId(null);
    } else {
      setConfirmRemoveId(acc.credentialId);
    }
  };

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1800);
  };

  const btn = (
    label: string,
    onClick: () => void,
    opts?: { disabled?: boolean; danger?: boolean; small?: boolean }
  ) => (
    <button
      type="button"
      onClick={onClick}
      disabled={opts?.disabled}
      style={{
        padding: opts?.small ? '4px 9px' : '7px 13px',
        background: opts?.danger ? '#fff0f0' : '#f0f5ff',
        border: `1px solid ${opts?.danger ? '#fca5a5' : '#93c5fd'}`,
        borderRadius: '6px',
        color: opts?.danger ? '#dc2626' : '#1d4ed8',
        fontSize: opts?.small ? '0.78em' : '0.86em',
        fontWeight: 500,
        cursor: opts?.disabled ? 'not-allowed' : 'pointer',
        opacity: opts?.disabled ? 0.5 : 1,
      }}
    >
      {label}
    </button>
  );

  // ── Compact single-account view (no account or exactly one account) ──────────
  const hasAccounts = accounts.length > 0;

  return (
    <div style={{
      borderTop: '1px solid #e5e7eb',
      paddingTop: '12px',
      marginTop: '8px',
      fontSize: '13px',
      color: '#111',
    }}>
      {/* ── Section label ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <span style={{ fontSize: '11px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          Smart Account (Passkey)
          <ExpertLink id={factoryId} />
        </span>
        {hasAccounts && (
          <button
            type="button"
            onClick={() => setShowManage(m => !m)}
            style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: '11px', cursor: 'pointer', padding: 0 }}
          >
            {showManage ? 'Done' : 'Manage'}
          </button>
        )}
      </div>

      {/* ── No account yet ── */}
      {!hasAccounts && (
        <div>
          <p style={{ margin: '0 0 8px 0', color: '#6b7280', fontSize: '12px', lineHeight: 1.4 }}>
            Link a passkey to enable biometric deposits without Freighter.
          </p>
          {error && <p style={{ color: '#dc2626', margin: '0 0 6px 0', fontSize: '12px' }}>{error}</p>}
          {btn(isRegistering ? 'Registering…' : '+ Register Passkey', handleRegister, { disabled: isRegistering })}
        </div>
      )}

      {/* ── Existing accounts ── */}
      {accounts.map((acc, i) => {
        const bal        = balances[acc.credentialId];
        const balXlm     = bal != null ? (bal / 1e7).toFixed(4) : '…';
        const isFundOpen = fundingId === acc.credentialId;
        const isConfirmRemove = confirmRemoveId === acc.credentialId;

        return (
          <div key={acc.credentialId} style={{ marginBottom: accounts.length > 1 ? '10px' : 0 }}>
            {/* Address + balance row */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 }}>
                <span style={{ fontSize: '11px', color: '#374151', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '130px' }} title={acc.accountAddress}>
                  {acc.accountAddress.slice(0, 6)}…{acc.accountAddress.slice(-4)}
                </span>
                <button
                  type="button"
                  onClick={() => handleCopy(acc.accountAddress, `addr-${acc.credentialId}`)}
                  title="Copy address"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', color: '#9ca3af', fontSize: '12px', lineHeight: 1, flexShrink: 0 }}
                >
                  {copiedId === `addr-${acc.credentialId}` ? '✓' : '⎘'}
                </button>
                <ExpertLink id={acc.accountAddress} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                <span style={{ fontSize: '12px', color: '#15803d', fontWeight: 700 }}>{balXlm} XLM</span>
                <button
                  type="button"
                  onClick={() => fetchBalance(acc)}
                  title="Refresh balance"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '13px', padding: 0, lineHeight: 1 }}
                >↺</button>
              </div>
            </div>

            {/* Fund shortcut (always visible) */}
            {!showManage && (
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                {!isFundOpen
                  ? btn('+ Add Funds', () => { setFundingId(acc.credentialId); setError(null); }, { small: true })
                  : (
                    <>
                      <input
                        type="number" min="0.01" step="1"
                        value={fundAmount}
                        onChange={e => setFundAmount(e.target.value)}
                        placeholder="XLM"
                        style={{ width: '64px', padding: '4px 7px', border: '1px solid #d1d5db', borderRadius: '5px', color: '#111', fontSize: '12px', background: '#fff' }}
                      />
                      <span style={{ color: '#6b7280', fontSize: '12px' }}>XLM</span>
                      {btn(isFunding ? 'Sending…' : 'Send from Wallet', () => handleFund(acc), { disabled: isFunding, small: true })}
                      {btn('Cancel', () => setFundingId(null), { small: true })}
                    </>
                  )
                }
              </div>
            )}

            {/* Manage mode */}
            {showManage && (
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '4px' }}>
                {btn(isFundOpen ? 'Cancel Fund' : '+ Add Funds', () => {
                  setFundingId(isFundOpen ? null : acc.credentialId);
                  setTransferFromId(null);
                  setError(null);
                }, { small: true })}
                {btn(transferFromId === acc.credentialId ? 'Cancel Transfer' : '⇄ Transfer', () => {
                  setTransferFromId(transferFromId === acc.credentialId ? null : acc.credentialId);
                  setFundingId(null);
                  setError(null);
                  setTransferDest('wallet');
                }, { small: true })}
                {btn(isConfirmRemove ? 'Confirm Remove' : 'Remove', () => handleRemove(acc), { small: true, danger: true })}
                {isConfirmRemove && btn('Cancel', () => setConfirmRemoveId(null), { small: true })}
              </div>
            )}

            {/* Fund form (manage mode) */}
            {showManage && isFundOpen && (
              <div style={{ marginTop: '6px', display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  type="number" min="0.01" step="1"
                  value={fundAmount}
                  onChange={e => setFundAmount(e.target.value)}
                  placeholder="Amount"
                  style={{ width: '64px', padding: '4px 7px', border: '1px solid #d1d5db', borderRadius: '5px', color: '#111', fontSize: '12px', background: '#fff' }}
                />
                <span style={{ color: '#6b7280', fontSize: '12px' }}>XLM</span>
                {btn(isFunding ? 'Sending…' : 'Send from Wallet', () => handleFund(acc), { disabled: isFunding, small: true })}
              </div>
            )}

            {/* Transfer form (manage mode) */}
            {showManage && transferFromId === acc.credentialId && (
              <div style={{ marginTop: '8px', padding: '8px', background: '#f0f5ff', border: '1px solid #bfdbfe', borderRadius: '6px' }}>
                <div style={{ fontSize: '11px', fontWeight: 600, color: '#1d4ed8', marginBottom: '6px' }}>Transfer from this account</div>
                <div style={{ marginBottom: '6px' }}>
                  <label style={{ fontSize: '11px', color: '#6b7280', display: 'block', marginBottom: '3px' }}>To</label>
                  <select
                    value={transferDest}
                    onChange={e => setTransferDest(e.target.value)}
                    disabled={isTransferring}
                    style={{ width: '100%', padding: '5px 7px', fontSize: '12px', borderRadius: '5px', border: '1px solid #d1d5db', background: '#fff', color: '#111' }}
                  >
                    <option value="wallet">Connected Wallet ({walletAddress.slice(0,6)}…{walletAddress.slice(-4)})</option>
                    {accounts.filter(a => a.credentialId !== acc.credentialId).map(other => (
                      <option key={other.credentialId} value={other.accountAddress}>
                        Smart Account {other.accountAddress.slice(0,6)}…{other.accountAddress.slice(-4)}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  <input
                    type="number" min="0.0000001" step="1"
                    value={transferAmount}
                    onChange={e => setTransferAmount(e.target.value)}
                    placeholder="Amount"
                    disabled={isTransferring}
                    style={{ width: '64px', padding: '4px 7px', border: '1px solid #d1d5db', borderRadius: '5px', color: '#111', fontSize: '12px', background: '#fff' }}
                  />
                  <span style={{ color: '#6b7280', fontSize: '12px' }}>XLM</span>
                  {btn(isTransferring ? 'Transferring…' : 'Sign & Send', () => handleTransfer(acc), { disabled: isTransferring, small: true })}
                </div>
                <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '4px' }}>Uses your passkey to authorise the transfer</div>
              </div>
            )}

            {/* Separator between multiple accounts */}
            {i < accounts.length - 1 && <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '8px 0' }} />}
          </div>
        );
      })}

      {/* Status messages */}
      {fundSuccess && <p style={{ color: '#15803d', margin: '6px 0 0', fontSize: '12px' }}>{fundSuccess}</p>}
      {transferSuccess && <p style={{ color: '#15803d', margin: '6px 0 0', fontSize: '12px' }}>{transferSuccess}</p>}
      {error && <p style={{ color: '#dc2626', margin: '6px 0 0', fontSize: '12px' }}>{error}</p>}

      {/* Add another passkey (manage mode) */}
      {showManage && accounts.length > 0 && (
        <div style={{ marginTop: '8px' }}>
          {btn(isRegistering ? 'Registering…' : '+ Add Another Passkey', handleRegister, { disabled: isRegistering })}
        </div>
      )}
    </div>
  );
};

export default PasskeyKitPanel;
